import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, MessageSquareText, Phone } from 'lucide-react';
import { User } from '@/api/entities';
import { buildTommyUrl, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '@/constants/support';
import ContactForm from '@/components/contact/ContactForm';
import PageSeo from '@/components/PageSeo';

export default function Contact() {
  const [userName, setUserName] = useState('');
  const location = useLocation();

  useEffect(() => {
    if (window.location.hash !== '#message') window.scrollTo(0, 0);
    
    // Get user's name from localStorage or API
    const loadUserName = async () => {
      try {
        const user = await User.me();
        if (user) {
          const fullName = user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.firstName || '';
          setUserName(fullName);
        }
      } catch (error) {
        console.error('Failed to load user name:', error);
      }
    };
    
    loadUserName();
  }, []);

  // /contact#message (linked from the Help Center, error messages and our
  // emails, in place of the old support address) opens at the form.
  useEffect(() => {
    if (location.hash === '#message') document.getElementById('message')?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  // The "Text Us" card used to open an SMS composer straight to the
  // concierge — the single biggest unconditional page-a-human path in the
  // app, right on the page customers land on when they want help. It now
  // points at Tommy, who answers most of it himself and escalates the rest
  // through his manager. See src/constants/support.js.
  const chatLink = buildTommyUrl();


  return (
    <div className="min-h-[70vh] bg-black text-white/80">
      <PageSeo path="/contact" />
      <div className="mx-auto max-w-4xl px-6 py-12">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <h1 className="text-3xl font-light text-white mb-2">Contact Us</h1>
        <p className="text-[13px] text-white/60 mb-12 leading-relaxed max-w-3xl">
          Get in touch with our concierge team for any inquiries or support.
        </p>

        {/* Two Column Layout - Side by side on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Team Photo */}
          <div>
            <img 
              src="/images/defera-team-1000.jpg"
              alt="Defera Team"
              className="w-full rounded-2xl border border-white/10"
              loading="lazy"
            />
          </div>

          {/* Contact Information */}
          <div className="space-y-6 text-sm">
            
            {/* Message Us — this was "Text Us", a direct SMS link to the
                concierge. It's the first thing a customer sees on the
                page they open when they need help, so it was also the
                app's widest-open door to paging a human. It now opens the
                concierge chat, which answers instantly, around the clock,
                and escalates what genuinely needs a person. */}
            <div className="flex items-start gap-4">
              <MessageSquareText className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="text-white mb-1 font-normal">Chat with Us</h2>
                <Link
                  to={chatLink}
                  className="text-white/85 hover:text-white transition-colors text-xs font-normal underline"
                >
                  Start a conversation
                </Link>
                <p className="text-white/40 text-xs mt-1">Concierge is available 24/7</p>
              </div>
            </div>

            {/* Call Us — the voice concierge line: the same specialist
                desk as chat, answering live around the clock. */}
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="text-white mb-1 font-normal">Call Us</h2>
                <a
                  href={SUPPORT_PHONE_TEL}
                  className="text-white/85 hover:text-white transition-colors text-xs font-normal"
                >
                  {SUPPORT_PHONE_DISPLAY}
                </a>
                <p className="text-white/40 text-xs mt-1">Concierge is available 24/7</p>
              </div>
            </div>

            {/* Write Us */}
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="text-white mb-1 font-normal">Write Us</h2>
                <p className="text-white/85 text-xs font-normal leading-relaxed">
                  Defera LLC<br />
                  28 Geary St, Suite 650 #321<br />
                  San Francisco, CA 94108
                </p>
              </div>
            </div>

          </div>

        </div>

        {/* Send us a message: replaces the published email address
            (founder, 2026-10-23). Signed-in messages land in the customer's
            own chat and on the team's desk; guests give an email to reply to. */}
        <div className="mt-12 max-w-2xl scroll-mt-24" id="message">
          <ContactForm defaultName={userName} />
        </div>

      </div>
    </div>
  );
}
